import { useCallback, useEffect, useRef } from 'react'

import { useAppSelector } from '@/redux/hooks'
import { ROLE } from '@/constants/role'
import { ua } from '@/methods/getUAData'

/** MilkyWay LOGGER_TYPE_MAP.WEBRTC.code */
const TRACKER_TYPE_WEBRTC = 27

/**
 * 正式環境 tracker（對齊 MilkyWay `https://apm${env}.tutormeet.com/tip.gif`，prod 時 env 為空）
 */
export const TRACKER_TIP_GIF_URL = 'https://apm.tutormeet.com/tip.gif'

/** 對齊 MilkyWay tip.js：query 一律帶 prod */
const TRACKER_ENV = 'prod'

/** 與 MilkyWay reportStatus.sendStatus 相同角色語意 */
export function liveRoleToReportRole(role: number): string {
  switch (role) {
    case ROLE.HOST:
      return 'HOST'
    case ROLE.IT:
      return 'IT'
    case ROLE.AUDIENCE:
      return 'STUDENT'
    case ROLE.GUEST:
      return 'GUEST'
    default:
      return 'UNKNOWN'
  }
}

export type ReportStatusSessionContext = {
  /** 上報 tip/info.sessionId：優先為直播間 roomId（含後備） */
  sessionId: string
  roleLabel: string
}

export type SendReportStatusPayload = {
  ctx: ReportStatusSessionContext
  token: string
  id: string
  key: string
  value: string
  detail?: string
}

function queryStringifyTip(params: Record<string, string>): string {
  return Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&')
}

/**
 * 對齊 MilkyWay tip.js：GET `tip.gif?id=...&info=...`（單筆事件立即送出），不依賴 Redux statusUrl。
 */
export function sendReportStatus(payload: SendReportStatusPayload): void {
  const { ctx, token, id, key, value, detail = '' } = payload
  if (typeof window === 'undefined') return

  const osName = ua.os?.name?.toLowerCase() ?? ''
  const terminal = osName.includes('mac') || osName.includes('ios') ? 'mac' : 'win'
  const version = [ua.browser?.name, ua.browser?.version].filter(Boolean).join(' ') || ''

  const subInfo = detail ? `${key}:${value},${detail}` : `${key}:${value}`

  const entry = {
    type: TRACKER_TYPE_WEBRTC,
    time: Date.now(),
    subInfo,
    info: {
      loginToken: token,
      env: TRACKER_ENV,
      sessionId: ctx.sessionId || '',
      role: ctx.roleLabel,
      terminal,
      version,
      id,
      key,
      value,
      detail,
    },
  }

  const qs = queryStringifyTip({
    id: window.location.href,
    info: JSON.stringify([entry]),
  })

  const url = `${TRACKER_TIP_GIF_URL}?${qs}`
  if ('DEBUG_REPORT_STATUS' in window) {
    console.log('[reportStatus][tip.gif]', url.slice(0, 512) + (url.length > 512 ? '…' : ''))
  }

  try {
    const img = new Image()
    img.onerror = () => {
      if ('DEBUG_REPORT_STATUS' in window) {
        console.warn('[reportStatus] tip.gif beacon failed', key, value)
      }
    }
    img.src = url
  } catch {
    if ('DEBUG_REPORT_STATUS' in window) {
      console.warn('[reportStatus] tip.gif throw', key, value, detail)
    }
  }
}

export function reportWebRtcPeerState(opts: {
  ctx: ReportStatusSessionContext
  token: string
  rtcPeer: RTCPeerConnection
  isLocal: boolean
  trigger: 'connection' | 'ice'
}): void {
  const { ctx, token, rtcPeer, isLocal, trigger } = opts
  const peerWithId = rtcPeer as RTCPeerConnection & { id?: string }
  const pcId = peerWithId.id ?? ''
  const prefix = isLocal ? 'WEBRTC_LOCAL' : 'WEBRTC_REMOTE'
  const key = trigger === 'connection' ? `${prefix}_CONNECTION` : `${prefix}_ICE`
  const stateValue = trigger === 'connection' ? rtcPeer.connectionState : rtcPeer.iceConnectionState
  const detail = JSON.stringify({
    connectionState: rtcPeer.connectionState,
    iceConnectionState: rtcPeer.iceConnectionState,
    signalingState: rtcPeer.signalingState,
    pcId,
    trigger,
  })
  sendReportStatus({
    ctx,
    token,
    id: pcId || token,
    key,
    value: stateValue,
    detail,
  })
}

/** 上報附帶 roomId（寫入 info.sessionId）與角色 */
export function useReportStatusContextGetter(): () => ReportStatusSessionContext {
  const reportInfo = useAppSelector((state) => state.socket.reportInfo)
  const socketRoomId = useAppSelector((state) => state.socket.roomId)
  const roomDataRoomId = useAppSelector((state) => state.room.roomData.roomId)
  const role = useAppSelector((state) => state.member.memberData?.role ?? ROLE.UNKNOWN)
  const sessionRef = useRef<ReportStatusSessionContext>({
    sessionId: '',
    roleLabel: 'UNKNOWN',
  })

  useEffect(() => {
    const sessionId =
      (roomDataRoomId && String(roomDataRoomId)) ||
      (socketRoomId && String(socketRoomId)) ||
      (reportInfo.sessionId && String(reportInfo.sessionId)) ||
      ''
    sessionRef.current = {
      sessionId,
      roleLabel: liveRoleToReportRole(role),
    }
  }, [roomDataRoomId, socketRoomId, reportInfo.sessionId, role])

  return useCallback(() => sessionRef.current, [])
}
